import React, {
  useEffect, useState, useMemo, useContext, useRef, forwardRef, useImperativeHandle, useCallback,
} from "react";

import {
  CheckOutlined, CloseOutlined, InfoCircleOutlined, PlusOutlined,
} from "@ant-design/icons";
import { Button, Input } from "antd";
import { filter, has, isEmpty } from "lodash";
import debounce from "lodash/debounce";
import Highlighter from "react-highlight-words";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import StyledPanelBadge from "../../../../Common/UIComponents/StyledPanelBadge";
import StyledScrollbar from "../../../../Common/UIComponents/StyledScrollbar";
import { showStyledToast } from "../../../../Common/UIComponents/StyledToast";
import StyledTooltip from "../../../../Common/UIComponents/StyledTooltip";
import SampleSubmissionContext from "../../../SampleSubmissionContext";
import RequestNewTestRedirectModal from "../../../SampleSubmissionModal/RequestNewTestRedirectModal";
import { CreateGroupModal } from "../../../TestDirectory/GroupsPanel";
import { CHAR_LIMITS, replicateTagRegexFirstMatch } from "../../Constant";
import PesticideSampleAutocomplete from "../../ReusableComponents/PesticideSampleAutocomplete";
import {
  belongsToPanel, findFullPanelTest, getPanelBadgeItems, getPanelInfo, getPanelMembers, getPanelSearchMatches,
  isFullPanel, isPanelMember, isSamePanel, PANEL_TYPE, shouldShowPanelSubtestRequestPrompt,
} from "./panelHelpers";
import PanelMatchesModal from "./PanelMatchesModal";
import PanelOverrideUndoNotice from "./PanelOverrideUndoNotice";
import PanelRelatedTestsModal from "./PanelRelatedTestsModal";

import {
  canAddSampleSubmissionTest,
  canAccessSampleSubmissionTestDirectory,
  canRequestTest,
  hasPermission,
  hasSampleSubmissionTestDirectoryFeature,
  permissions,
} from "../../../../../utils/Permissions";
import getTestName, {
  getGroupName,
  getGroupId,
  getGroupTests,
  groupTestsByLevel,
} from "../../../TestDirectory/helpers";
import {
  isTestNotDoableForSide as testNotDoableForSide,
  TEST_DIRECTORY_PATH,
} from "../../../utils";

import { getTestList } from "../../../../../actions/sampleSubmission";
import {
  createCustomerGroup,
  CUSTOMER_GROUP_TYPE,
  deleteCustomerGroup,
  getCustomerGroups,
  // getTestDirectory,
  // TEST_SUGGESTION_CATEGORY,
  updateCustomerGroup,
} from "../../../../../actions/testDirectory";

import { ReactComponent as StackHalfFillIcon } from "../../../../../assets/images/icons/stack-half-fill.svg";
import { ReactComponent as StackSolidIcon } from "../../../../../assets/images/icons/stack-solid.svg";
import { ReactComponent as ExclamationIcon } from "../../../../../assets/images/sample-submission/exclamationIcon.svg";
import searchIcon from "../../../../../assets/images/sample-submission/searchIcon.svg";

import "./AnalysisRequest.css";
import "../SampleDetails/SampleDetails.css";

/* Quick Apply / My Groups demo bundles — kept for reference.
 * Quick Apply UI is commented out below; My Groups uses /customertests/.
const QUICK_APPLY_DEMO_PACKAGES = [
  {
    id: "gmo-cert",
    title: "GMO Certification Package",
    tests: ["GMO Screen", "35S Promoter", "NOS Terminator", "CryIA(b)", "CP4 EPSP"],
  },
  {
    id: "food-safety",
    title: "Food Safety Essentials",
    tests: ["APC", "Yeast & Mold", "Coliform", "Salmonella"],
  },
];
const MY_GROUPS_DEMO_GROUPS = [
  {
    id: "microbiology",
    title: "Microbiology",
    tests: ["APC", "Yeast & Mold", "Coliform"],
  },
  {
    id: "pathogen-screen",
    title: "Pathogen Screen",
    tests: ["Salmonella", "Listeria", "E. coli O157"],
  },
];
*/

const AnalysisRequest = forwardRef((props, ref) => {
  const {
    clearTestSearch,
    setClearTestSearch,
    testsSelected,
    setTestsSelected,
    customerDetails,
    submissionEditingSample,
    sampleID,
    disableAddSample,
    setInvalidTest,
    testInfoMap,
    pesticideSample,
    setPesticideSample,
    inputValuesRefs,
    setInputValuesRefs,
    setInvalidInputFound,
    sampleCategory,
    AddUpdateSampleButton,
    setShowExitModal,
    setEditingSubmittedSampleComposite,
    onSaveDraftForRequestNew,
  } = props;

  const [search, setSearch] = useState("");
  const [searched, setSearched] = useState("");
  const [testSuggestions, setTestSuggestions] = useState([]);
  const [openMatches, setOpenMatches] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTestTab, setActiveTestTab] = useState("browse");
  const [customerGroups, setCustomerGroups] = useState([]);
  const [showRequestRedirectModal, setShowRequestRedirectModal] = useState(false);
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [savingCreateGroup, setSavingCreateGroup] = useState(false);
  const [savingGroupModal, setSavingGroupModal] = useState(false);
  const [savingDraftForRequest, setSavingDraftForRequest] = useState(false);
  const [pendingRequestTestName, setPendingRequestTestName] = useState("");
  const [panelDialogPanelName, setPanelDialogPanelName] = useState(null);
  const [panelPendingKeys, setPanelPendingKeys] = useState([]);
  const [overrideNotice, setOverrideNotice] = useState(null);
  // const [manuallyAddingTest, setManuallyAddingTest] = useState(true);
  const testSuggestionsScrollbar = useRef();
  const isMountedRef = useRef(true);
  const { isLoggedOut, thirdParty, mrl } = useContext(SampleSubmissionContext);
  const history = useHistory();

  const customerIDForApi = useMemo(
    () => ((isLoggedOut && !thirdParty) ? customerDetails.customerID : undefined),
    [isLoggedOut, thirdParty, customerDetails.customerID],
  );

  const showTestDirectoryTabs = hasSampleSubmissionTestDirectoryFeature();
  const showBrowsePanel = !showTestDirectoryTabs || activeTestTab === "browse";
  const showGroupsPanel = showTestDirectoryTabs && activeTestTab === "groups";

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      setClearTestSearch(false);
    };
  }, [setClearTestSearch]);

  const loadCustomerGroups = useCallback(async () => {
    const { success, groups, message } = await getCustomerGroups("", customerIDForApi);
    if (!isMountedRef.current) return;
    if (!success) {
      toast.error(message || "Unable to load groups.");
      setCustomerGroups([]);
      return;
    }
    const nextGroups = (groups || []).map((group, index) => {
      const title = getGroupName(group, index);
      const groupId = getGroupId(group);
      const tests = getGroupTests(group).map(getTestName).filter(Boolean);
      return {
        id: groupId || group.id || group._id || title,
        group_id: groupId || undefined,
        title,
        tests,
        group_name: group.group_name || group.name || title,
        group_type: group.group_type || group.type || CUSTOMER_GROUP_TYPE,
        type: group.type || group.group_type || CUSTOMER_GROUP_TYPE,
      };
    });
    setCustomerGroups(nextGroups);
  }, [customerIDForApi]);

  useEffect(() => {
    if (!showTestDirectoryTabs) {
      setCustomerGroups([]);
    }
  }, [showTestDirectoryTabs]);

  /** Refetch /customertests/ whenever My Groups opens (Test Directory Groups tab pattern). */
  useEffect(() => {
    if (!showTestDirectoryTabs || activeTestTab !== "groups") return undefined;
    loadCustomerGroups();
    return undefined;
  }, [activeTestTab, showTestDirectoryTabs, loadCustomerGroups]);

  // Expose a method to get the current testsSelected
  useImperativeHandle(ref, () => ({
    getTestsSelected: () => testsSelected,
  }));

  /** Get test suggestions based on search input */
  const apiGetTestSuggestions = useCallback(async (searchInput) => {
    const { success, message, testList } = await getTestList(
      searchInput,
      customerIDForApi,
      sampleCategory,
    );
    if (!isMountedRef.current) {
      return;
    }
    if (success) {
      setTestSuggestions(Array.isArray(testList) ? testList : []);
      // Browse Tests fallback (pending confirmation — keep commented until flow is approved):
      // Empty customer-mapping + no Test Directory feature → fall back to test-directory
      // (same /testsuggestions/ category pattern as Test Directory Unlock).
      // let resolvedTests = Array.isArray(testList) ? testList : [];
      // if (
      //   resolvedTests.length === 0
      //   && !hasSampleSubmissionTestDirectoryFeature()
      // ) {
      //   const directoryResult = await getTestDirectory(
      //     searchInput,
      //     sampleCategory,
      //     customerIDForApi,
      //     undefined,
      //     TEST_SUGGESTION_CATEGORY.TEST_DIRECTORY,
      //   );
      //   if (!isMountedRef.current) {
      //     return;
      //   }
      //   if (directoryResult.success) {
      //     resolvedTests = Array.isArray(directoryResult.tests) ? directoryResult.tests : [];
      //   }
      // }
      // setTestSuggestions(resolvedTests);
      setSearched(searchInput);
      if (testSuggestionsScrollbar.current) {
        testSuggestionsScrollbar.current.scrollerElement.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
    } else {
      toast.error(message);
    }
    setIsLoading(false);
  }, [customerIDForApi, sampleCategory]);

  useEffect(() => {
    const sampleIDHasReplicate = replicateTagRegexFirstMatch.test(sampleID);
    const invalidTests = filter(testsSelected, (testObj, name) => (
      (testObj.replicated && sampleIDHasReplicate)
      || testNotDoableForSide(testObj, sampleCategory)
      || testNotDoableForSide(testInfoMap.get(name), sampleCategory)
    ));
    setInvalidTest(invalidTests.length > 0);
  }, [testsSelected, sampleID, sampleCategory, testInfoMap]); // eslint-disable-line

  useEffect(() => {
    setOpenMatches(null);
  }, [searched]);

  /** Add debouncing feature for the apiGetTestSuggestions function
   * @return {Function} apiGetTestSuggestions function with debouncing feature,
   * invoking apiGetTestSuggestions only after 500ms has passed after the last invocation.
   */
  const debouncedSearch = useMemo(
    () => debounce((searchInput) => {
      apiGetTestSuggestions(searchInput);
    }, 500),
    [apiGetTestSuggestions],
  );

  /** Initialize suggestions from tests fetched in SubmissionForm */
  useEffect(() => {
    if (search === "" && testInfoMap?.size > 0) {
      setTestSuggestions(Array.from(testInfoMap.values()));
      setSearched("");
    }
    setIsLoading(false);
  }, [testInfoMap, search]);

  /** After user adds a new sample, saves an edited sample, cancels editing, switches the editing sample to another,
   * "clearTestSearch" will be set from false to true. Reset search synchronously; avoid racing with user typing. */
  useEffect(() => {
    if (!clearTestSearch) {
      return;
    }
    debouncedSearch.cancel();
    setSearch("");
    setSearched("");
    setClearTestSearch(false);
    setOpenMatches(null);
    apiGetTestSuggestions("");
  }, [clearTestSearch, debouncedSearch, apiGetTestSuggestions, setClearTestSearch]);

  /** Cancel debouncing so after the component is unmounted, the debouncedSearch function will not be executed,
  and state will not be set causing an error. */
  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    // eslint-disable-next-line consistent-return
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]); // eslint-disable-line

  /** Call debounced search when search input is changed */
  const handleChangeSearch = (e) => {
    setSearch(e.target.value);
    setOpenMatches(null);
    debouncedSearch(e.target.value);
  };

  const handleClearSearch = () => {
    debouncedSearch.cancel();
    setSearch("");
    setSearched("");
    setOpenMatches(null);
    apiGetTestSuggestions("");
  };

  /** Search immediately after the user hits enter key */
  const handleHitEnter = (e) => {
    if (e.key === "Enter") {
      debouncedSearch.cancel();
      apiGetTestSuggestions(e.target.value);
    }
  };

  /**
   * Whether a test cannot be selected for the current sample side.
   * Uses product_doable / Environmental_doable from /testsuggestions/.
   * Missing flags are treated as doable (enabled).
   */
  const isTestNotDoableForSide = (testObj, name) => (
    testNotDoableForSide(testObj, sampleCategory)
    || (name ? testNotDoableForSide(testInfoMap.get(name), sampleCategory) : false)
  );

  /**
   * Add or remove a test from the selected tests
   * @param {String} test test name or test obj
   * @param {String} action If action is "deselect", then the test will be removed.
   * Otherwise, the test is added or removed depending on if the selected test set contains that test.
   * Clear all the selected tests
   */
  const toggleTestChoice = (_test, action) => {
    let test = _test;
    if (typeof test === "object") {
      test = _test.name;
    }

    if (action === "clear") {
      setTestsSelected({});
      if (submissionEditingSample) {
        setShowExitModal(true);
      }
      return;
    }

    // Block adding tests that are not doable for the current sample side
    if (action !== "deselect") {
      const testObj = typeof _test === "object" ? _test : testInfoMap.get(test);
      const alreadySelected = has(testsSelected, test);
      if (!alreadySelected && isTestNotDoableForSide(testObj)) {
        return;
      }
    }

    let openPanelDialogName = null;
    setTestsSelected((prev) => {
      const next = { ...prev };
      if (action === "deselect" || has(next, test)) {
        delete next[test];
        return next;
      }

      next[test] = typeof _test === "object"
        ? { ..._test }
        : { ...(testInfoMap.get(test) ?? {}) };
      next[test].autofilled = false;

      const addedTestObj = next[test];
      if (isPanelMember(addedTestObj)) {
        const { parentDisplayName } = getPanelInfo(addedTestObj);
        const selectedSiblingCount = Object.values(next).filter(
          (obj) => isPanelMember(obj) && isSamePanel(obj, addedTestObj),
        ).length;
        if (selectedSiblingCount >= 2) {
          openPanelDialogName = parentDisplayName;
        }
      }
      return next;
    });

    if (openPanelDialogName) {
      setPanelDialogPanelName(openPanelDialogName);
    }
    if (submissionEditingSample) {
      setShowExitModal(true);
    }
  };

  /**
  * Get text for test tooltip
  * @param {Array} display list of display values ex: [{ title: "Method", value: ""}]
  * @param {Boolean} replicated if test is replicated
  * @param {Boolean} sampleIDHasReplicate if sample has a replicate tag
  * @param {Boolean} [notDoableForSide] if test is not doable for current sample side
  * @returns Text containing test method, reporting limit, turnaround time if they exist and the reason for why the button is disabled
  */
  const toolTipText = (display, replicated, sampleIDHasReplicate, notDoableForSide = false) => {
    const textLines = [];
    if (!canAddSampleSubmissionTest()) {
      textLines.push("Only an admin or editor can add/edit tests.");
    }
    if (sampleIDHasReplicate && replicated) { // disable test
      textLines.push("Disabled: Cannot add a replicate test to a sample that is replicated");
    }
    if (notDoableForSide) {
      textLines.push(
        sampleCategory === "environment"
          ? "Cannot be performed on environment samples"
          : "Cannot be performed on product samples",
      );
    }
    (Array.isArray(display) ? display : []).forEach(({ title, value }) => {
      if (value) {
        textLines.push(`${title}: ${value}`);
      }
    });
    return textLines.join("\n");
  };

  const analysisClassName = (disableSelection, name) => {
    let className = "";
    if (disableSelection) {
      className = "disabled-test-suggestion";
    } else if (has(testsSelected, name)) {
      className = "selected-test-suggestion";
    } else {
      className = "unselected-test-suggestion";
    }
    return className;
  };

  /**
   * Updates the provided tests by adding a selectMRLType property to MRL tests.
   * The first encountered MRL test will have selectMRLType set to true, while subsequent MRL tests will have selectType set to false.
   * @param {Object} testsSelected - An object containing test objects with MRL property
   * @return {Object} The updated tests object with selectMRLType property added to MRL tests
   */
  const updatedTestsSelectedWithSelectMRLType = () => {
    let mrlFound = false;

    const updatedTests = Object.fromEntries(
      Object.entries(testsSelected).map(([key, testObj]) => {
        if (testObj.test_category === "MRL") {
          // If MRL is found and this is the first one, set selectType to true
          const selectMRLType = !mrlFound;
          mrlFound = true; // Mark that we've encountered the first MRL
          return [key, { ...testObj, selectMRLType }];
        }

        // Return the test object as is for non-MRL tests
        return [key, testObj];
      }),
    );
    return updatedTests;
  };

  /**
   * Disable pesticides tests which are not selected
   * @param {*} name Pesticide test name
   * @returns {boolean}
   */
  const isMrlRestrictionActive = () => (
    sampleCategory !== "environment" && (hasPermission(permissions.global.specialPesticides) || mrl)
  );

  const isMrlTestName = (name) => testInfoMap.get(name)?.test_category === "MRL";

  const disableNonSelectedPesticideTests = (name) => {
    if (isEmpty(testsSelected)) {
      return false;
    }
    const testNames = Object.keys(testsSelected);
    const addedPesticideTestName = testNames.find((test) => isMrlTestName(test));
    return addedPesticideTestName && addedPesticideTestName !== name && isMrlRestrictionActive() && isMrlTestName(name);
  };

  const wouldExceedSingleMrlPerSample = (bundleTestNames) => {
    if (!isMrlRestrictionActive()) return false;
    const mrlAfterApply = new Set([
      ...Object.keys(testsSelected).filter(isMrlTestName),
      ...(bundleTestNames || []).filter(isMrlTestName),
    ]);
    return mrlAfterApply.size > 1;
  };

  let sampleIDHasReplicate = false;
  if (replicateTagRegexFirstMatch.test(sampleID)) sampleIDHasReplicate = true;

  const disableTestListMod = !canAddSampleSubmissionTest();
  const canOpenRequestNewTest = canAccessSampleSubmissionTestDirectory();

  const groupedTestSuggestions = useMemo(
    () => groupTestsByLevel(testSuggestions),
    [testSuggestions],
  );

  const showPanelSubtestRequestPrompt = useMemo(
    () => shouldShowPanelSubtestRequestPrompt(testSuggestions, searched)
      && !isLoggedOut
      && !thirdParty
      && !disableTestListMod
      && canOpenRequestNewTest,
    [
      testSuggestions,
      searched,
      isLoggedOut,
      thirdParty,
      disableTestListMod,
      canOpenRequestNewTest,
    ],
  );

  const handleApplyPanel = (panelTest) => {
    if (disableTestListMod) return;
    if (isTestNotDoableForSide(panelTest)) return;
    const panelName = panelTest?.name;
    if (!panelName || has(testsSelected, panelName)) return;

    const pendingKey = `panel::${panelName}`;
    setPanelPendingKeys((prev) => (prev.includes(pendingKey) ? prev : [...prev, pendingKey]));

    window.setTimeout(() => {
      if (!isMountedRef.current) return;

      let removedEntries = [];
      setTestsSelected((prev) => {
        removedEntries = Object.entries(prev).filter(
          ([, obj]) => belongsToPanel(obj, panelName),
        );
        const next = { ...prev };
        removedEntries.forEach(([name]) => { delete next[name]; });
        next[panelName] = { ...panelTest, autofilled: false };
        return next;
      });
      setPanelPendingKeys((prev) => prev.filter((k) => k !== pendingKey));
      if (removedEntries.length > 0) {
        setOverrideNotice({
          panelName,
          removedNames: removedEntries.map(([name]) => name),
          removedEntries,
        });
      }
    }, 500);

    if (submissionEditingSample) {
      setShowExitModal(true);
    }
  };

  const handleDialogAddIndividual = () => {
    setPanelDialogPanelName(null);
  };

  const handleDialogAddFullPanel = () => {
    const panelTest = panelDialogPanelName ? findFullPanelTest(panelDialogPanelName, testInfoMap) : null;
    if (panelTest) {
      handleApplyPanel(panelTest);
    } else if (panelDialogPanelName) {
      toast.error(`Unable to find the ${panelDialogPanelName} panel test.`);
    }
    setPanelDialogPanelName(null);
  };

  const handleDialogRemoveSelected = (name) => {
    toggleTestChoice(name, "deselect");
  };

  const handleDialogAddOther = (name) => {
    const testObj = testInfoMap.get(name);
    if (testObj) {
      toggleTestChoice(testObj);
    }
  };

  const getDisablingPanelName = (testObj) => {
    const owner = Object.values(testsSelected).find(
      (obj) => isFullPanel(obj) && belongsToPanel(testObj, obj.name),
    );
    return owner ? owner.name : null;
  };

  const openRequestNewTestFlow = () => {
    if (!canOpenRequestNewTest) return;
    setPendingRequestTestName(search.trim());
    setShowRequestRedirectModal(true);
  };

  const handleContinueCurrentForm = () => {
    if (savingDraftForRequest) return;
    setShowRequestRedirectModal(false);
    setPendingRequestTestName("");
  };

  const redirectToRequestNewTest = useCallback((meta) => {
    const testName = meta.testName || pendingRequestTestName || search.trim();
    const params = new URLSearchParams({
      tab: "test-request",
      "request-new-test": "true",
      "test-name": testName,
    });
    setShowRequestRedirectModal(false);
    setPendingRequestTestName("");
    history.push(`${TEST_DIRECTORY_PATH}?${params.toString()}`);
  }, [history, pendingRequestTestName, search]);

  const handleConfirmAddNewTest = async () => {
    if (savingDraftForRequest) return;
    const testName = pendingRequestTestName || search.trim();
    if (typeof onSaveDraftForRequestNew === "function") {
      setSavingDraftForRequest(true);
      const result = await onSaveDraftForRequestNew(testName);
      setSavingDraftForRequest(false);
      if (!result?.success) return;
      if (!result.submissionID) {
        toast.error("Draft saved without an ID. Please try again.");
        return;
      }
    }
    redirectToRequestNewTest({ testName });
  };

  const isBundleFullyApplied = (bundle) => (
    bundle.tests.length > 0 && bundle.tests.every((name) => has(testsSelected, name))
  );

  const handleApplyBundle = (bundle) => {
    if (disableTestListMod) return;
    if (wouldExceedSingleMrlPerSample(bundle.tests)) {
      toast.error("This group cannot be added because only one MRL test can be selected per sample.");
      return;
    }
    const testsSelectedCopy = { ...testsSelected };
    bundle.tests.forEach((testName) => {
      if (!has(testsSelectedCopy, testName)) {
        testsSelectedCopy[testName] = { ...(testInfoMap.get(testName) ?? {}) };
        testsSelectedCopy[testName].autofilled = false;
      }
    });
    setTestsSelected(testsSelectedCopy);
    if (submissionEditingSample) {
      setShowExitModal(true);
    }
  };

  const handleRemoveBundle = (bundle) => {
    if (disableTestListMod) return;
    const testsSelectedCopy = { ...testsSelected };
    bundle.tests.forEach((testName) => {
      delete testsSelectedCopy[testName];
    });
    setTestsSelected(testsSelectedCopy);
    if (submissionEditingSample) {
      setShowExitModal(true);
    }
  };

  const renderGroupAction = (bundle, applied) => {
    if (disableTestListMod) {
      return (
        <StyledTooltip title="Only an admin or editor can add/edit tests" placement="left">
          <span
            className={`AnalysisRequest__GroupApplyBtn AnalysisRequest__GroupApplyBtn--disabled ${applied ? "AnalysisRequest__GroupApplyBtn--applied" : ""}`}
            aria-hidden
          >
            {applied ? <CheckOutlined /> : <PlusOutlined />}
          </span>
        </StyledTooltip>
      );
    }
    if (applied) {
      return (
        <button
          type="button"
          className="AnalysisRequest__GroupApplyBtn AnalysisRequest__GroupApplyBtn--applied"
          onClick={() => handleRemoveBundle(bundle)}
          aria-label={`Remove ${bundle.title} group`}
        >
          <CheckOutlined />
        </button>
      );
    }
    return (
      <button
        type="button"
        className="AnalysisRequest__GroupApplyBtn"
        onClick={() => handleApplyBundle(bundle)}
        aria-label={`Apply ${bundle.title} group`}
      >
        <PlusOutlined />
      </button>
    );
  };

  const handleOpenNewGroup = () => {
    if (!canRequestTest()) return;
    loadCustomerGroups();
    setShowCreateGroupModal(true);
  };

  const handleCloseCreateGroupModal = useCallback(() => {
    setShowCreateGroupModal(false);
    loadCustomerGroups();
  }, [loadCustomerGroups]);

  const handleCreateGroup = async ({ group_name, test_list }) => {
    setSavingCreateGroup(true);
    const { success, message } = await createCustomerGroup({
      group_name,
      test_list,
      group_type: CUSTOMER_GROUP_TYPE,
    });
    if (!isMountedRef.current) return false;
    setSavingCreateGroup(false);
    if (!success) {
      toast.error(message || "Unable to create group.");
      return false;
    }
    toast.success(message || "Group created successfully.");
    await loadCustomerGroups();
    return true;
  };

  const handleRenameGroup = async (group, nextName) => {
    const currentName = getGroupName(group);
    const groupId = getGroupId(group);
    const test_list = getGroupTests(group).map(getTestName).filter(Boolean);
    setSavingGroupModal(true);
    const { success, message } = await updateCustomerGroup({
      group_id: groupId,
      prev_group: groupId ? undefined : currentName,
      group_name: nextName,
      test_list,
      group_type: group.group_type || group.type || CUSTOMER_GROUP_TYPE,
    });
    if (!isMountedRef.current) return false;
    setSavingGroupModal(false);
    if (!success) {
      toast.error(message || "Unable to rename group.");
      return false;
    }
    toast.success(message || "Group renamed.");
    await loadCustomerGroups();
    return true;
  };

  const handleDeleteGroup = async (group) => {
    const currentName = getGroupName(group);
    const groupId = getGroupId(group);
    setSavingGroupModal(true);
    const { success, message } = await deleteCustomerGroup({
      group_id: groupId,
      group_name: groupId ? undefined : currentName,
      group_type: group.group_type || group.type || CUSTOMER_GROUP_TYPE,
    });
    if (!isMountedRef.current) return;
    setSavingGroupModal(false);
    if (!success) {
      toast.error(message || "Unable to delete group.");
      return;
    }
    toast.success(message || "Group deleted.");
    await loadCustomerGroups();
  };

  const handleUpdateGroupTests = async (group, test_list) => {
    const currentName = getGroupName(group);
    const groupId = getGroupId(group);
    const normalizedList = (Array.isArray(test_list) ? test_list : [])
      .map(getTestName)
      .filter(Boolean);
    setSavingGroupModal(true);
    const { success, message } = await updateCustomerGroup({
      group_id: groupId,
      prev_group: groupId ? undefined : currentName,
      group_name: currentName,
      test_list: normalizedList,
      group_type: group.group_type || group.type || CUSTOMER_GROUP_TYPE,
    });
    if (!isMountedRef.current) return false;
    setSavingGroupModal(false);
    if (!success) {
      toast.error(message || "Unable to update group tests.");
      return false;
    }
    await loadCustomerGroups();
    return true;
  };

  /* Quick Apply — renderBundleAction disabled with tab/panel
  const renderBundleAction = (bundle, applied, applyLabel) => {
    if (disableTestListMod) {
      return (
        <StyledTooltip title="Only an admin or editor can add/edit tests" placement="left">
          <span className={`AnalysisRequest__QuickApplyActionMuted ${applied ? "AnalysisRequest__QuickApplyActionMuted--applied" : ""}`}>
            {applied ? "✓ Applied" : applyLabel}
          </span>
        </StyledTooltip>
      );
    }
    if (applied) {
      return (
        <button
          type="button"
          className="AnalysisRequest__QuickApplyAppliedBtn"
          onClick={() => handleRemoveBundle(bundle)}
        >
          ✓ Applied
        </button>
      );
    }
    return (
      <button
        type="button"
        className="AnalysisRequest__QuickApplyApplyBtn"
        onClick={() => handleApplyBundle(bundle)}
      >
        {applyLabel}
      </button>
    );
  };
  */

  return (
    <div className="new-submission-pages-card analysis-request">
      <RequestNewTestRedirectModal
        open={showRequestRedirectModal}
        loading={savingDraftForRequest}
        onContinueForm={handleContinueCurrentForm}
        onAddNewTest={handleConfirmAddNewTest}
      />
      <CreateGroupModal
        open={showCreateGroupModal}
        saving={savingCreateGroup || savingGroupModal}
        sampleCategory={sampleCategory}
        groups={customerGroups}
        onClose={handleCloseCreateGroupModal}
        onCreate={handleCreateGroup}
        onRename={handleRenameGroup}
        onDelete={handleDeleteGroup}
        onUpdateTests={handleUpdateGroupTests}
      />
      {disableAddSample && (<div className="SampleSubmissionForm__DisableAddSample" />)}
      {showTestDirectoryTabs && (
      <div className="AnalysisRequest__TabBar TestDirectory__SectionTabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={activeTestTab === "browse"}
          className={`AnalysisRequest__Tab TestDirectory__SectionTab ${activeTestTab === "browse" ? "TestDirectory__SectionTab--active" : ""}`}
          onClick={() => setActiveTestTab("browse")}
        >
          Browse Tests
        </button>
        {/* Quick Apply — disabled for now; re-enable tab + panel below later
        <button
          type="button"
          role="tab"
          aria-selected={activeTestTab === "quick"}
          className={`AnalysisRequest__Tab ${activeTestTab === "quick" ? "AnalysisRequest__Tab--active" : ""}`}
          onClick={() => setActiveTestTab("quick")}
        >
          Quick Apply
        </button>
        */}
        <button
          type="button"
          role="tab"
          aria-selected={activeTestTab === "groups"}
          className={`AnalysisRequest__Tab TestDirectory__SectionTab ${activeTestTab === "groups" ? "TestDirectory__SectionTab--active" : ""}`}
          onClick={() => setActiveTestTab("groups")}
        >
          My Groups
        </button>
      </div>
      )}
      {/* eslint-disable react/jsx-indent */}
      {showBrowsePanel && (
      <div className="AnalysisRequest__TabPanel AnalysisRequest__TabPanel--browse">
        <div className="AnalysisRequest__BrowseColumn">
          <div className="AnalysisRequest__SearchRow">
            <div className="analysis-request-search-box">
              <Input
                allowClear
                placeholder="Search in Test Directory"
                value={search}
                onChange={handleChangeSearch}
                onClear={handleClearSearch}
                onKeyUp={handleHitEnter}
                maxLength={CHAR_LIMITS.analysis_request_search}
                className="AnalysisRequest__SearchInput"
                prefix={<img alt="" src={searchIcon} aria-hidden />}
              />
            </div>
            {showPanelSubtestRequestPrompt && (
              <div
                className="AnalysisRequest__PanelSubtestRequest"
                role="region"
                aria-label="Request a test not shown in results"
              >
                <span className="AnalysisRequest__PanelSubtestRequestLabel">
                  Can&apos;t find your test?
                </span>
                <Button
                  type="default"
                  className="AnalysisRequest__AddRequestBtn AnalysisRequest__PanelSubtestRequestBtn"
                  htmlType="button"
                  onClick={openRequestNewTestFlow}
                >
                  Add a request
                </Button>
              </div>
            )}
          </div>
          {!isLoading && testSuggestions.length === 0 && searched.trim() && (
            <div className="AnalysisRequest__NoResultCard" role="region" aria-live="polite">
              <div className="AnalysisRequest__NoResultContent">
                <div className="AnalysisRequest__NoResultTitle">No result found!</div>
                {!thirdParty && (
                  <p className="AnalysisRequest__NoResultSubtitle">
                    Can&apos;t find your test? Request to add a new one to the directory.
                  </p>
                )}
                {!isLoggedOut && !thirdParty && !disableTestListMod && canOpenRequestNewTest && (
                  <Button
                    type="default"
                    className="AnalysisRequest__AddRequestBtn"
                    htmlType="button"
                    onClick={openRequestNewTestFlow}
                  >
                    Add a request
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>

        {!(!isLoading && testSuggestions.length === 0 && searched.trim()) && (
        <>
      <div className="analysis-request-smart-suggestions-container">
        {!isLoading && (
          testSuggestions.length > 0 ? (
            <StyledScrollbar ref={testSuggestionsScrollbar}>
              <div className="AnalysisRequest__TestLevelGroups">
                {groupedTestSuggestions.map(({ key: levelKey, label, tests }) => {
                  const orderedTests = searched.trim()
                    ? [...tests].sort((a, b) => {
                      const aRank = isFullPanel(a) ? 0 : 1;
                      const bRank = isFullPanel(b) ? 0 : 1;
                      return aRank - bRank;
                    })
                    : tests;

                  return (
                    <section
                      key={levelKey || "my-tests"}
                      className="AnalysisRequest__TestLevelSection"
                      aria-label={label}
                    >
                      <h5 className="AnalysisRequest__TestLevelHeader">{label}</h5>
                      <ul className="analysis-request-smart-suggestions-list">
                        {orderedTests.map((testObj, i) => {
                          const { name, replicated, display } = testObj;
                          const panelBadgeItems = getPanelBadgeItems(testObj, "browse");
                          const isPanel = isFullPanel(testObj);
                          const alreadySelected = has(testsSelected, name);
                          const panelDisablingName = getDisablingPanelName(testObj);
                          const notDoableForSide = isTestNotDoableForSide(testObj);
                          const disableTest = (sampleIDHasReplicate && replicated) || disableTestListMod
                            || disableNonSelectedPesticideTests(name) || Boolean(panelDisablingName)
                            || notDoableForSide;
                          const handleRowClick = () => {
                            if (isPanel) {
                              if (alreadySelected) {
                                toggleTestChoice(name, "deselect");
                              } else {
                                handleApplyPanel(testObj);
                              }
                            } else {
                              toggleTestChoice(testObj);
                            }
                          };
                          const {
                            matchNames: panelMatchNames,
                            matchCount: panelMatchCount,
                            totalCount: panelMatchTotal,
                          } = (isPanel && searched.trim())
                            ? getPanelSearchMatches(testObj, searched)
                            : { matchNames: [], matchCount: 0, totalCount: 0 };
                          const matchesOpen = openMatches?.name === name;
                          return (
                            <StyledTooltip
                              key={`${name}-${i}`}
                              title={matchesOpen ? "" : toolTipText(display, replicated, sampleIDHasReplicate, notDoableForSide)}
                              placement="right"
                              open={matchesOpen ? false : undefined}
                            >
                              <li
                                onClick={disableTest ? null : handleRowClick}
                                className={analysisClassName(disableTest, name)}
                              >
                                <span className="AnalysisRequest__SuggestionMain">
                                  {isPanel && (
                                    <StackSolidIcon className="AnalysisRequest__SuggestionIcon" aria-hidden />
                                  )}
                                  <Highlighter
                                    highlightClassName="searchHighlight"
                                    searchWords={[searched.trim()]}
                                    caseSensitive={false}
                                    autoEscape
                                    textToHighlight={name}
                                  />
                                  {panelBadgeItems.map((badge, badgeIndex) => (
                                    <StyledPanelBadge
                                      key={`${name}-panel-${badge.text}-${badgeIndex}`}
                                      text={badge.text}
                                      textColor={badge.textColor}
                                      backgroundColor={badge.backgroundColor}
                                      context="browse"
                                      icon={badge.icon}
                                      panelType={badge.panelType}
                                    />
                                  ))}
                                </span>
                                {panelMatchCount > 0 && (
                                  <span
                                    className="AnalysisRequest__MatchesBadge"
                                    role="button"
                                    tabIndex={0}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setOpenMatches({
                                        name,
                                        matchCount: panelMatchCount,
                                        matchTotal: panelMatchTotal,
                                        matchNames: panelMatchNames,
                                        searchQuery: searched,
                                      });
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setOpenMatches({
                                          name,
                                          matchCount: panelMatchCount,
                                          matchTotal: panelMatchTotal,
                                          matchNames: panelMatchNames,
                                          searchQuery: searched,
                                        });
                                      }
                                    }}
                                  >
                                    <span className="AnalysisRequest__MatchesBadgeText">
                                      {panelMatchCount}
                                      {" Matches"}
                                    </span>
                                    <InfoCircleOutlined className="AnalysisRequest__MatchesBadgeIcon" />
                                  </span>
                                )}
                              </li>
                            </StyledTooltip>
                          );
                        })}
                      </ul>
                    </section>
                  );
                })}
              </div>

            </StyledScrollbar>
          ) : (
            <div className="AnalysisRequest__NoSuggestionsContainer">
              <span className="analysis-request-no-tests-found">No results found</span>
            </div>
          ))}
      </div>
        </>
        )}
      </div>
      )}
      {/* eslint-enable react/jsx-indent */}
      {/* Quick Apply panel — disabled for now; do not wire to My Groups /customertests/
      {activeTestTab === "quick" && (
        <div className="AnalysisRequest__QuickApply">
          {customerGroups.length === 0 && (
            <div className="AnalysisRequest__TabPlaceholder">No packages available</div>
          )}
          {customerGroups.map((pkg) => {
            const applied = isBundleFullyApplied(pkg);
            return (
              <div key={pkg.id} className="AnalysisRequest__QuickApplyCard">
                <div className="AnalysisRequest__QuickApplyCardHeader">
                  <span className="AnalysisRequest__QuickApplyTitle">
                    {pkg.title}
                    {" "}
                    (
                    {pkg.tests.length}
                    )
                  </span>
                  {renderBundleAction(pkg, applied, "+ Apply Package")}
                </div>
                <div className="AnalysisRequest__QuickApplyPills">
                  {pkg.tests.map((t) => (
                    <span key={t} className="AnalysisRequest__QuickApplyPill">{t}</span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
      */}
      {showGroupsPanel && (
        <div className="AnalysisRequest__TabPanel AnalysisRequest__TabPanel--groups">
          {!isLoggedOut && !thirdParty && canOpenRequestNewTest && (
            <div className="AnalysisRequest__MyGroupsHeader">
              <button
                type="button"
                className="AnalysisRequest__NewGroupLink"
                onClick={handleOpenNewGroup}
              >
                + New Group
              </button>
            </div>
          )}
          <div className="analysis-request-smart-suggestions-container">
            {customerGroups.length === 0 ? (
              <div className="AnalysisRequest__NoSuggestionsContainer">
                <span className="analysis-request-no-tests-found">No groups found</span>
              </div>
            ) : (
              <StyledScrollbar
                noScrollX
                className="AnalysisRequest__MyGroupsScrollbar"
              >
                <div className="AnalysisRequest__QuickApply AnalysisRequest__MyGroups">
                  {customerGroups.map((group) => {
                    const applied = isBundleFullyApplied(group);
                    return (
                      <div key={group.id} className="AnalysisRequest__QuickApplyCard">
                        <div className="AnalysisRequest__QuickApplyCardHeader">
                          <span className="AnalysisRequest__QuickApplyTitle">
                            {group.title}
                            {" "}
                            (
                            {group.tests.length}
                            )
                          </span>
                          {renderGroupAction(group, applied)}
                        </div>
                        <div className="AnalysisRequest__QuickApplyPills">
                          {group.tests.map((t) => (
                            <span key={t} className="AnalysisRequest__QuickApplyPill">{t}</span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </StyledScrollbar>
            )}
          </div>
        </div>
      )}
      <>
        {/* {search && !disableTestListMod && <ManuallyAddTestSection testsSelected={testsSelected} toggleTestChoice={toggleTestChoice} />} */}
        <div className="analysis-request-tests-selected-and-checkbox-header">
          <div className="analysis-request-tests-selected-header-div">
            <h4 className="analysis-request-tests-selected-header">{`Tests Selected (${Object.keys(testsSelected).length})`}</h4>
            <h4
              className="analysis-request-tests-selected-clear"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleTestChoice("", "clear");
              }}
            >
              Clear All
            </h4>
          </div>
          {/* Re-enable with saveTestsForNext / setSaveTestsForNext props from SampleDetailsPage
            {!hideSaveForNext && (
              <div className="sample-submission-checkbox-container analysis-requested-checkbox-container">
                <input id="tests-use-in-next-checkbox" type="checkbox" checked={saveTestsForNext ?? false} onChange={() => setSaveTestsForNext(!saveTestsForNext)} />
                <span aria-hidden className="sample-submission-checkmark" onClick={() => setSaveTestsForNext(!saveTestsForNext)} />
                <label htmlFor="tests-use-in-next-checkbox">Use in Next Sample</label>
              </div>
            )}
          */}
        </div>
        <div className="analysis-request-tests-selected-container">
          {!isLoading && (
            <>
              {!isEmpty(testsSelected)
                ? (
                  <StyledScrollbar>
                    <ul className="analysis-request-tests-selected-list">
                      {Array.from(Object.entries(updatedTestsSelectedWithSelectMRLType())).map(([name, testObj]) => {
                        const notDoableForSide = isTestNotDoableForSide(testObj, name);
                        return (
                          <div key={name}>
                            <StyledTooltip
                              title={toolTipText(
                                testObj.display || testInfoMap.get(name)?.display || [],
                                testObj.replicated,
                                sampleIDHasReplicate,
                                notDoableForSide,
                              )}
                              placement="right"
                            >
                              {testObj.test_category === "MRL" && testObj.selectMRLType && sampleCategory !== "environment" && (hasPermission(permissions.global.specialPesticides) || mrl) ? (
                                <PesticideSampleAutocomplete
                                  testName={name}
                                  testObj={testObj}
                                  disableTestListMod={disableTestListMod}
                                  toggleTestChoice={toggleTestChoice}
                                  sampleIDHasReplicate={sampleIDHasReplicate}
                                  notDoableForSide={notDoableForSide}
                                  pesticideSample={pesticideSample}
                                  setPesticideSample={setPesticideSample}
                                  inputValuesRefs={inputValuesRefs}
                                  setInputValuesRefs={setInputValuesRefs}
                                  setInvalidInputFound={setInvalidInputFound}
                                  sampleCategory={sampleCategory}
                                />
                              ) : (() => {
                                const selectedPanelInfo = getPanelInfo(testObj);
                                const selectedPanelBadgeItems = getPanelBadgeItems(testObj, "selected");
                                const isPanelRelated = selectedPanelInfo.type !== PANEL_TYPE.NORMAL;
                                const isSelectedFullPanel = selectedPanelInfo.type === PANEL_TYPE.FULL_PANEL;
                                return (
                                  <li
                                    key={name}
                                    className={`analysis-request-tests-selected-list-item ${(sampleIDHasReplicate && testObj.replicated) || notDoableForSide ? "AnalysisRequest__InvalidTest" : ""}`}
                                  >
                                    {(testObj.test_category === "MRL" && (hasPermission(permissions.global.specialPesticides) || mrl)) && (
                                    <StyledTooltip
                                      title={`Sample Type: ${pesticideSample}`}
                                      placement="top"
                                    >
                                      <ExclamationIcon className="AnalysisRequest__InfoIcon" width={14} height={14} />
                                    </StyledTooltip>
                                    )}
                                    <span className="AnalysisRequest__SelectedTestLabel">
                                      {isPanelRelated && (
                                        isSelectedFullPanel
                                          ? <StackSolidIcon className="AnalysisRequest__SelectedPanelIcon" aria-hidden />
                                          : <StackHalfFillIcon className="AnalysisRequest__SelectedPanelIcon" aria-hidden />
                                      )}
                                      {name}
                                      {selectedPanelBadgeItems.map((badge, badgeIndex) => (
                                        <React.Fragment key={`${name}-selected-panel-${badge.text}-${badgeIndex}`}>
                                          <span className="AnalysisRequest__SelectedTestSeparator">·</span>
                                          <StyledPanelBadge
                                            text={badge.text}
                                            context="selected"
                                            panelType={badge.panelType}
                                            icon={false}
                                          />
                                        </React.Fragment>
                                      ))}
                                    </span>
                                    {!disableTestListMod && (
                                    <div
                                      className="AnalysisRequest__CloseIcon"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleTestChoice(name, "deselect");
                                      }}
                                    >
                                      <CloseOutlined />
                                    </div>
                                    )}
                                  </li>
                                );
                              })()}
                            </StyledTooltip>
                          </div>
                        );
                      })}
                      {panelPendingKeys.map((key) => (
                        <div key={key}>
                          <li className="analysis-request-tests-selected-list-item AnalysisRequest__SelectedSkeleton ActiveloadingAnimation" aria-hidden />
                        </div>
                      ))}
                    </ul>
                  </StyledScrollbar>
                )
                : <div className="no-tests-added">No tests added</div>}
            </>
          )}
        </div>
      </>
      {(() => {
        const dialogPanelName = panelDialogPanelName;
        const dialogMemberNames = dialogPanelName
          ? getPanelMembers(dialogPanelName, testInfoMap).map((t) => t.name)
          : [];
        const selectedMemberNames = dialogMemberNames.filter((n) => has(testsSelected, n));
        const otherMemberNames = dialogMemberNames.filter((n) => !has(testsSelected, n));
        return (
          <PanelRelatedTestsModal
            open={Boolean(dialogPanelName) && selectedMemberNames.length >= 2}
            panelName={dialogPanelName}
            selectedMemberNames={selectedMemberNames}
            otherMemberNames={otherMemberNames}
            totalMemberCount={dialogMemberNames.length}
            onRemoveSelected={handleDialogRemoveSelected}
            onAddOther={handleDialogAddOther}
            onAddFullPanel={handleDialogAddFullPanel}
            onAddIndividual={handleDialogAddIndividual}
            onClose={() => setPanelDialogPanelName(null)}
          />
        );
      })()}
      <PanelMatchesModal
        open={Boolean(openMatches)}
        panelName={openMatches?.name}
        matchCount={openMatches?.matchCount}
        matchTotal={openMatches?.matchTotal}
        matchNames={openMatches?.matchNames}
        searchQuery={openMatches?.searchQuery}
        onClose={() => setOpenMatches(null)}
      />
      <PanelOverrideUndoNotice
        open={Boolean(overrideNotice)}
        panelName={overrideNotice?.panelName}
        removedNames={overrideNotice?.removedNames || []}
        onUndo={() => {
          setTestsSelected((prev) => {
            const next = { ...prev };
            delete next[overrideNotice.panelName];
            overrideNotice.removedEntries.forEach(([name, obj]) => { next[name] = obj; });
            return next;
          });
          setOverrideNotice(null);
        }}
        onClose={() => setOverrideNotice(null)}
        onConfirm={() => {
          const panelName = overrideNotice?.panelName;
          setOverrideNotice(null);
          if (panelName) {
            const name = String(panelName || "").trim() || "Panel";
            showStyledToast({
              variant: "success",
              message: `${name} added Successfully`,
            });
          }
        }}
      />
      <AddUpdateSampleButton setEditingSubmittedSampleComposite={setEditingSubmittedSampleComposite} />
    </div>
  );
});

export default AnalysisRequest;
